/** Galvanised dry-feed hopper that straddles a fattening-pen wall. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone } from '@/render/palette';

function fatteningSurfaces(): readonly [number, number, number, number, number] {
  const surfaces = getZone('fattening')?.surfaces;
  if (!surfaces || surfaces.length < 5) throw new Error('fattening zone needs five surface colours');
  return [surfaces[0]!, surfaces[1]!, surfaces[2]!, surfaces[3]!, surfaces[4]!];
}

function box(
  size: readonly [number, number, number],
  colour: number,
  name: string,
): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), surface('metal', colour));
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function taperedHopperGeometry(): THREE.BufferGeometry {
  const positions = new Float32Array([
    -0.08, 0.42, -0.375, 0.08, 0.42, -0.375, 0.08, 0.42, 0.375, -0.08, 0.42, 0.375,
    -0.275, 1.25, -0.375, 0.275, 1.25, -0.375, 0.275, 1.25, 0.375, -0.275, 1.25, 0.375,
  ]);
  const indices = [
    0, 1, 5, 0, 5, 4, 1, 2, 6, 1, 6, 5,
    2, 3, 7, 2, 7, 6, 3, 0, 4, 3, 4, 7,
    0, 3, 2, 0, 2, 1, 4, 5, 6, 4, 6, 7,
  ];
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  // No `uv` here on purpose: `instanceGroup` supplies one for any hand-built
  // geometry before it merges, because nothing in this project is textured and
  // an asset author should not have to know the instancer exists. See the long
  // note there — it is the reason this hall once refused to boot.
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function addTrough(group: THREE.Group, x: number, side: 'left' | 'right', steel: number): void {
  const floor = box([0.2, 0.025, 0.75], steel, `trough-${side}-floor`);
  floor.position.set(x, 0.3125, 0);
  const innerWall = box([0.025, 0.1, 0.75], steel, `trough-${side}-inner-lip`);
  innerWall.position.set(x + (side === 'left' ? 0.0875 : -0.0875), 0.35, 0);
  const outerWall = box([0.025, 0.1, 0.75], steel, `trough-${side}-outer-lip`);
  outerWall.position.set(x + (side === 'left' ? -0.0875 : 0.0875), 0.35, 0);
  group.add(floor, innerWall, outerWall);
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'feed-hopper';
  const [shadow, , steel, , highlight] = fatteningSurfaces();

  addTrough(group, -0.175, 'left', steel);
  addTrough(group, 0.175, 'right', steel);

  const hopper = new THREE.Mesh(taperedHopperGeometry(), surface('metal', steel));
  hopper.name = 'tapered-sheet-hopper';
  hopper.castShadow = true;
  hopper.receiveShadow = true;
  group.add(hopper);

  const lid = box([0.59, 0.05, 0.79], highlight, 'flat-lid');
  lid.position.y = 1.275;
  // Across the lid's back edge, which runs along X and is 0.59 long — not along
  // Z and not 0.75. Built the other way first, which put a bar sticking 0.375 m
  // out of the back of the hopper and threw the asset's Z centre off by nearly
  // half its own depth. Caught by `asset.spec.ts`'s centring assertion, which is
  // exactly the failure that check exists for.
  const hinge = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.59, 8), surface('metal', shadow));
  hinge.name = 'lid-hinge-bar';
  hinge.rotation.z = Math.PI / 2;
  hinge.position.set(0, 1.275, -0.36);
  group.add(lid, hinge);

  const legSplay = 0.22;
  // Rotating a rectangular leg makes its lowest corner sit below its unrotated
  // half-height. This is the rotated vertical extent, so every foot still meets
  // the ground datum instead of quietly floating above it.
  const legCentreY = (0.42 * Math.cos(legSplay) + 0.045 * Math.sin(legSplay)) / 2;
  for (const [x, z, name] of [
    [-0.185, -0.29, 'leg-back-left'], [0.185, -0.29, 'leg-back-right'],
    [-0.185, 0.29, 'leg-front-left'], [0.185, 0.29, 'leg-front-right'],
  ] as const) {
    const leg = box([0.045, 0.42, 0.045], steel, name);
    leg.position.set(x, legCentreY, z);
    leg.rotation.z = x < 0 ? -legSplay : legSplay;
    group.add(leg);
  }

  const tube = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 1.0, 12), surface('metal', steel));
  tube.name = 'feed-drop-tube';
  tube.position.y = 1.8;
  const collar = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.06, 12), surface('metal', shadow));
  collar.name = 'plain-drop-collar';
  collar.position.y = 2.27;
  group.add(tube, collar);

  return group;
}

export const feedHopperAsset: AssetDefinition = {
  id: 'feed-hopper',
  label: 'Dry-feed hopper',
  description: 'Wall-straddling galvanised hopper with paired feed troughs and a drop tube.',
  footprint: { width: 0.55, height: 2.30, length: 0.75 },
  create: build,
};
